// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel, { SwiperStyle } from "@conklin/App/Components/XngCarousel";
import { xTabEnableZone } from "@conklin/App/Handlers/XNG/Common";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import ProductCarouselProduct from "@conklin/Overrides/Components/ProductCarouselProduct";
import ProductCarouselProductCondensed from "@conklin/Overrides/Components/ProductCarouselProductCondensed";
import { productsScreenRegex, SLIDES_PER_VIEW_POINTS } from "@conklin/XDL/constants";
import { generateSlidesPerViewMap } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    purchaseType = "purchaseType",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    // XNG-Change
    showTitle = "showTitle",
    showImage = "showImage",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    responsive = "responsive",
    viewType = "viewType",
    numOfItemsXS = "numOfItemsXS",
    numOfItemsSM = "numOfItemsSM",
    numOfItemsMD = "numOfItemsMD",
    numOfItemsLG = "numOfItemsLG",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: string;
        [fields.purchaseType]: string;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        // XNG-Change
        [fields.showTitle]: boolean;
        [fields.showImage]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.responsive]: boolean;
        [fields.viewType]: string;
        [fields.numOfItemsXS]: string;
        [fields.numOfItemsSM]: string;
        [fields.numOfItemsMD]: string;
        [fields.numOfItemsLG]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        canViewPurchasedProducts: isAuthenticated,
        // XNG-Change
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
    // XNG-Changes
    xTabEnableZone,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductsStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headerContainer?: GridContainerProps;
    itemHeaderGridItem?: GridItemProps;
    itemHeaderText?: TypographyPresentationProps;
    priceHeaderGridItem?: GridItemProps;
    priceHeaderText?: TypographyPresentationProps;
    addToCartHeaderGridItem?: GridItemProps;
    addToCartHeaderText?: TypographyPresentationProps;
    // XNG-Changes
    swiper?: SwiperStyle;
}

export const purchasedProductsStyles: PurchasedProductsStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    headerContainer: {
        gap: 0,
        css: css`
            padding: 10px;
            background-color: ${getColor("common.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    itemHeaderGridItem: { width: [0, 0, 6, 6, 6] },
    itemHeaderText: { weight: "bold" },
    priceHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-right: 35px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    priceHeaderText: { weight: "bold" },
    addToCartHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
        `,
    },
    addToCartHeaderText: { weight: "bold" },
};

const styles = purchasedProductsStyles;

const getJustifyContent = (position: string) => {
    if (position === "left") {
        return "flex-start";
    }
    if (position === "right") {
        return "flex-end";
    }
    return "center";
};

const TitleWrapper = styled.div<{ position: string }>`
    display: flex;
    justify-content: ${({ position }) => getJustifyContent(position)};
`;

const PurchasedProducts = ({
    id,
    fields,
    products,
    canViewPurchasedProducts,
    loadPurchasedProducts,
    // XNG-Changes
    zone,
    isTabletScreen,
    xTabEnableZone,
}: Props) => {
    React.useEffect(() => {
        if (canViewPurchasedProducts) {
            loadPurchasedProducts({
                widgetId: id,
                purchaseType: fields.purchaseType,
            });
        }
    }, [fields.purchaseType]);

    // XNG-Changes
    React.useEffect(() => {
        if (products && products.length > -1 && zone.includes("tab")) {
            xTabEnableZone({ zone, isEnabled: products.length > 0 });
        }
    }, [products]);

    if (!canViewPurchasedProducts || !products || products.length === 0) {
        return null;
    }

    // <XNG-Change>
    const CarouselCard = fields.viewType === "Condensed" ? ProductCarouselProductCondensed : ProductCarouselProduct;
    const breakpoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: parseFloat(fields.numOfItemsXS) || 2,
        [SLIDES_PER_VIEW_POINTS.sm]:
            parseFloat(fields.numOfItemsSM) || (fields.viewType === "Condensed" || isTabletScreen ? 2.25 : 2.8),
        [SLIDES_PER_VIEW_POINTS.md]: parseFloat(fields.numOfItemsMD) || 3,
        [SLIDES_PER_VIEW_POINTS.lg]: parseFloat(fields.numOfItemsLG) || 3.8,
    });
    // </XNG-Change>

    // XNG-Change: change structure to carousel
    return (
        <StyledWrapper className={`carousel-container-purchased${id}`}>
            {fields.title && (
                <TitleWrapper {...styles.titleWrapper} position={fields.titlePosition}>
                    <Typography forwardAs="p" {...styles.titleText}>
                        {fields.title}
                    </Typography>
                </TitleWrapper>
            )}
            <XngCarousel
                spaceBetween={fields.viewType === "Condensed" || isTabletScreen ? 22 : 24}
                navigation={!isTabletScreen}
                pagination={false}
                breakpoints={fields.responsive ? undefined : breakpoints}
                draggable={false}
                scrollbar
                className={!isTabletScreen ? "xng-swiper-with-shadow" : ""}
                extendedStyles={styles.swiper}
                arrowNavigationType="circle"
            >
                {products.map(product => (
                    <CarouselCard
                        key={product.id}
                        product={product}
                        showBrand={fields.showBrand}
                        showPartNumbers={fields.showPartNumbers}
                        showPrice={fields.showPrice}
                        showAvailability={fields.showAvailability}
                        showAddToCart={fields.showAddToCart}
                        showAddToList={fields.showAddToList}
                        carouselId={id}
                        showTitle={fields.showTitle}
                        showImage={fields.showImage}
                        showImageBadges={fields.showImageBadges}
                        maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                        showTextBadges={fields.showTextBadges}
                        maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                    />
                ))}
            </XngCarousel>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedProducts),
    definition: {
        group: "Common",
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.titlePosition,
                displayName: "Title Position",
                editorTemplate: "DropDownField",
                options: [
                    { value: "center", displayName: "Center" },
                    { value: "left", displayName: "Left" },
                    { value: "right", displayName: "Right" },
                ],
                defaultValue: "center",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
                sortOrder: 2,
            },
            // XNG-Change
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 3,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 4,
            },
            {
                name: fields.showBrand,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            // XNG-Change
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 11,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "3",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 12,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 13,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "1",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 14,
            },
            // <XNG-Changes>
            {
                name: fields.viewType,
                displayName: "View Type",
                editorTemplate: "DropDownField",
                defaultValue: "Regular",
                options: [
                    { displayName: "Regular View", value: "Regular" },
                    { displayName: "Condensed View", value: "Condensed" },
                ],
                fieldType: "Contextual",
                sortOrder: 23,
                hideEmptyOption: true,
                tab: advancedTab,
            },
            {
                name: fields.responsive,
                displayName: "Responsive mode",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
                tab: advancedTab,
            },
            {
                name: fields.numOfItemsXS,
                displayName: "Products on Screens < 480px  ",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: advancedTab,
                validate: (value, item) => {
                    if (value === "") {
                        return "";
                    }
                    if (!productsScreenRegex.test(value)) {
                        return "Value should be from 1 - 6";
                    }
                    return "";
                },
            },
            {
                name: fields.numOfItemsSM,
                displayName: "Products on Screens < 1024px  ",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: advancedTab,
                validate: (value, item) => {
                    if (value === "") {
                        return "";
                    }
                    if (!productsScreenRegex.test(value)) {
                        return "Value should be from 1 - 6";
                    }
                    return "";
                },
            },
            {
                name: fields.numOfItemsMD,
                displayName: "Products on Screens < 1366px  ",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: advancedTab,
                validate: (value, item) => {
                    if (value === "") {
                        return "";
                    }
                    if (!productsScreenRegex.test(value)) {
                        return "Value should be from 1 - 6";
                    }
                    return "";
                },
            },
            {
                name: fields.numOfItemsLG,
                displayName: "Products on Screens > 1366px  ",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: advancedTab,
                validate: (value, item) => {
                    if (value === "") {
                        return "";
                    }
                    if (!productsScreenRegex.test(value)) {
                        return "Value should be from 1 - 6";
                    }
                    return "";
                },
            },
            // </XNG-Changes>
        ],
    },
};

export default widgetModule;
